import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiPlus, FiSend, FiPlay, FiAlertTriangle, FiLoader, FiCpu, FiX, FiSearch, FiBarChart2, FiCalendar } from 'react-icons/fi';
import { supabase } from '../../supabase';
import { useAuth } from '../../AuthContext';
import CONFIG from '../../config';
import { createBacktestConfig, fetchBacktestConfigs } from '../../lib/api/backtestConfigs';
import './HomePage.css';
import PremiumSignupBanner from '../PremiumSignupBanner.jsx';
import HistorySection from '../history/HistorySection';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE ||
  CONFIG.API_BASE ||
  'http://localhost:4998';
const MAX_MESSAGE_CHARS = 1000;
const EXAMPLE_PROMPTS = [
  {
    id: 'screen-low-pe',
    text: 'Screen for stocks with a low but positive price-earnings ratio',
    icon: FiSearch
  },
  {
    id: 'backtest-low-dividend-growth',
    text: 'Backtest a strategy: buy the 20 stocks with the lowest dividend yield, but only if yearly dividend growth is at least 5%',
    icon: FiPlay
  },
  {
    id: 'optimizer-btc-halving',
    text: 'I buy BTCUSD at the halving. How many days later should I sell, historically, to maximize returns?',
    icon: FiPlay
  },
  {
    id: 'chart-aapl-ps',
    text: 'Plot a chart with the AAPL stock price history, and the Price-to-sales ratio below',
    icon: FiBarChart2
  },
  {
    id: 'seasonality-gold-monthly',
    text: 'Analyze the monthly Gold seasonality',
    icon: FiCalendar
  }
];

const STRATEGY_DRAFT_PREFIX = 'strategy-draft:';

const createLocalStrategyDraft = (strategyConfig, name) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  const draftId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const key = `${STRATEGY_DRAFT_PREFIX}${draftId}`;
  const payload = {
    name: name || 'AI Strategy',
    strategy_config: strategyConfig || {},
    created_at: new Date().toISOString()
  };
  try {
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch (err) {
    return null;
  }
  return key;
};

const HomePage = () => {
  const { currentUser, isPremium } = useAuth();
  const location = useLocation();
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [truncatedNotice, setTruncatedNotice] = useState(false);
  const [actionResults, setActionResults] = useState({});
  const [aiQuota, setAiQuota] = useState(null);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const processedActionsRef = useRef(new Set());

  const activeChat = useMemo(
    () => chats.find((c) => c.id === selectedChatId),
    [chats, selectedChatId]
  );

  const parseJson = useCallback(async (res) => {
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      throw new Error(`Expected JSON from ${res.url || 'request'} (status ${res.status}). Body: ${text.slice(0, 200)}`);
    }
  }, []);

  const getSessionHeaders = useCallback(async (includeJson = false) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session');
    }
    const headers = { Authorization: `Bearer ${session.access_token}` };
    if (includeJson) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  }, []);

  const createChat = useCallback(async (selectAfter = true) => {
    const headers = await getSessionHeaders(true);
    const res = await fetch(`${API_BASE}/api/chats`, {
      method: 'POST',
      headers,
      body: JSON.stringify({}) // avoid 400 when Content-Type is set
    });
    const data = await parseJson(res);
    if (!res.ok) {
      throw new Error(data.message || 'Failed to create chat');
    }
    setChats((prev) => [data.chat, ...prev]);
    if (selectAfter) {
      setSelectedChatId(data.chat.id);
      setMessages([]);
    }
    return data.chat;
  }, [getSessionHeaders, parseJson]);

  const loadChats = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const headers = await getSessionHeaders();
      const res = await fetch(`${API_BASE}/api/chats`, { headers });
      const data = await parseJson(res);
      if (!res.ok) {
        throw new Error(data.message || 'Failed to load chats');
      }
      const list = data.chats || [];
      setChats(list);
    } catch (err) {
      setError(err.message || 'Unable to load chats');
    } finally {
      setLoading(false);
    }
  }, [getSessionHeaders, parseJson]);

  const fetchQuota = useCallback(async () => {
    try {
      const headers = await getSessionHeaders();
      const res = await fetch(`${API_BASE}/quota`, { headers });
      const data = await parseJson(res);
      if (!res.ok) {
        throw new Error(data.message || 'Failed to load AI credits');
      }
      setAiQuota(data);
    } catch (err) {
      console.error('Unable to load AI quota:', err);
    }
  }, [getSessionHeaders, parseJson]);

  const handleSend = async (evt, overrideMessage) => {
    evt?.preventDefault();
    if (sending) return;
    const trimmed = (overrideMessage ?? input).trim();
    if (!trimmed) return;
    if (!suggestionsDismissed) {
      setSuggestionsDismissed(true);
    }

    try {
      setSending(true);
      setError('');
      setTruncatedNotice(false);

      let chatId = selectedChatId;
      if (!chatId) {
        const newChat = await createChat(true);
        chatId = newChat?.id;
      }
      const headers = await getSessionHeaders(true);
      const res = await fetch(`${API_BASE}/api/chats/${chatId}/messages`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: trimmed })
      });
      const data = await parseJson(res);
      if (data?.ai_credits) {
        setAiQuota(data.ai_credits);
      }
      if (!res.ok) {
        throw new Error(data.message || 'Failed to send message');
      }

      setMessages(data.messages || []);
      if (data.chat) {
        setChats((prev) => {
          const others = prev.filter((c) => c.id !== data.chat.id);
          return [data.chat, ...others];
        });
        setSelectedChatId(data.chat.id);
      }
      setInput('');
      setTruncatedNotice(Boolean(data.truncated_input));
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleHistoryChatDeleted = useCallback((chatId) => {
    setChats((prev) => {
      const filtered = prev.filter((chat) => chat.id !== chatId);
      if (selectedChatId === chatId) {
        setSelectedChatId(null);
        setMessages([]);
        setSuggestionsDismissed(false);
        setLoadingConversation(false);
      }
      return filtered;
    });
  }, [selectedChatId]);

  const handleHistoryChatSelected = useCallback((chatId) => {
    if (chatId === selectedChatId) {
      return;
    }
    setSelectedChatId(chatId);
    setMessages([]);
    setSuggestionsDismissed(true);
    setLoadingConversation(true);
  }, [selectedChatId]);

  const handleStartNewChat = useCallback(() => {
    setSelectedChatId(null);
    setMessages([]);
    setInput('');
    setError('');
    setTruncatedNotice(false);
    setSuggestionsDismissed(false);
    setLoadingConversation(false);
    setActionResults({});
    processedActionsRef.current = new Set();
  }, []);

  const executeAction = useCallback(async (message) => {
    const action = (message?.action || '').toLowerCase();
    if (!action || action === 'none') return;
    const isOptimizerAction = action === 'optimizer';

    const strategyPayload = message?.strategy_configuration;
    const strategyConfig = strategyPayload?.strategy_config || strategyPayload;
    const backtestPayload = message?.backtest_configuration;
    const chartPayload = message?.chart_configuration
      || message?.chartConfiguration
      || message?.chart_config
      || message?.chart;
    const chartConfig = chartPayload?.chart_config
      || chartPayload?.chartConfig
      || chartPayload?.config
      || chartPayload;
    const seasonalityPayload = message?.seasonality_configuration
      || message?.seasonalityConfiguration
      || message?.seasonality_config
      || message?.seasonality;
    const seasonalityConfig = seasonalityPayload?.seasonality_config
      || seasonalityPayload?.seasonalityConfig
      || seasonalityPayload?.config
      || seasonalityPayload;
    const researchPayload = message?.research_configuration
      || message?.researchConfiguration
      || message?.research_config
      || message?.research;
    const researchConfig = researchPayload?.research_config
      || researchPayload?.config
      || researchPayload;
    const researchCode = researchConfig?.code || researchConfig?.script;
    const backtestConfigIdFromPayload = backtestPayload?.backtest_config_id
      || backtestPayload?.backtestConfigId
      || backtestPayload?.config_id
      || backtestPayload?.id
      || null;
    const inlineBacktestConfig = backtestPayload?.backtest_config || backtestPayload?.config;
    const isInlineConfigObject = !backtestConfigIdFromPayload && backtestPayload && typeof backtestPayload === 'object'
      && (
        backtestPayload.backtest
        || backtestPayload.run
        || backtestPayload.weighting
        || backtestPayload.rebalance
        || backtestPayload.costs
      );
    const backtestConfig = inlineBacktestConfig || (isInlineConfigObject ? backtestPayload : null);
    const optimizerPayloadSource = message?.optimizer_configuration
      || message?.optimizerConfiguration
      || message?.optimizer_config
      || message?.optimizer
      || backtestPayload?.optimizer_configuration
      || backtestPayload?.optimizerConfiguration
      || backtestPayload?.optimizer_config
      || backtestPayload?.optimizer;
    const optimizerConfig = optimizerPayloadSource?.optimizer_config
      || optimizerPayloadSource?.optimizerConfig
      || optimizerPayloadSource?.config
      || optimizerPayloadSource;
    let optimizerPayload = null;
    let optimizerError = null;
    if (optimizerConfig && typeof optimizerConfig === 'object') {
      const start = Number(optimizerConfig.start);
      const end = Number(optimizerConfig.end);
      const step = Number(optimizerConfig.step);
      if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(step) || step === 0) {
        optimizerError = 'Optimizer range requires numeric start, end, and step values.';
      } else {
        optimizerPayload = { start, end, step };
        if (optimizerConfig.target) optimizerPayload.target = optimizerConfig.target;
        if (optimizerConfig.direction) optimizerPayload.direction = optimizerConfig.direction;
      }
    }

    if ((action === 'screen' || action === 'strategy_configuration' || action === 'backtest' || action === 'optimizer') && !strategyConfig) {
      setActionResults((prev) => ({
        ...prev,
        [message.id]: { status: 'error', type: action, error: 'No strategy_configuration returned with the action.' }
      }));
      return;
    }
    if (action === 'chart' && !chartConfig) {
      setActionResults((prev) => ({
        ...prev,
        [message.id]: { status: 'error', type: action, error: 'No chart_configuration returned with the action.' }
      }));
      return;
    }
    if (action === 'seasonality' && !seasonalityConfig) {
      setActionResults((prev) => ({
        ...prev,
        [message.id]: { status: 'error', type: action, error: 'No seasonality_configuration returned with the action.' }
      }));
      return;
    }
    if (action === 'research' && !researchCode) {
      setActionResults((prev) => ({
        ...prev,
        [message.id]: { status: 'error', type: action, error: 'No research_configuration returned with the action.' }
      }));
      return;
    }
    if (optimizerError) {
      setActionResults((prev) => ({
        ...prev,
        [message.id]: { status: 'error', type: action, error: optimizerError }
      }));
      return;
    }
    if (isOptimizerAction && !optimizerPayload) {
      setActionResults((prev) => ({
        ...prev,
        [message.id]: { status: 'error', type: action, error: 'No optimizer_configuration returned with the action.' }
      }));
      return;
    }

    const loadingType = (isOptimizerAction || (action === 'backtest' && optimizerPayload)) ? 'optimizer' : action;
    setActionResults((prev) => ({
      ...prev,
      [message.id]: { status: 'loading', type: loadingType }
    }));

    try {
      const headers = await getSessionHeaders(true);

      if (action === 'strategy_configuration') {
        const draftName = strategyPayload?.name || message?.chat_name || 'AI Strategy';
        const draftKey = createLocalStrategyDraft(strategyConfig, draftName);
        if (!draftKey) {
          throw new Error('Unable to store strategy draft locally.');
        }
        setActionResults((prev) => ({
          ...prev,
          [message.id]: {
            status: 'success',
            type: 'strategy',
            data: { name: draftName },
            draftKey,
            link: `/dashboard/strategies?draft=${encodeURIComponent(draftKey)}`
          }
        }));
        return;
      }

      if (action === 'screen') {
        const resp = await fetch(`${API_BASE}/api/run-screen`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            strategy: strategyConfig,
            name: strategyPayload?.name || message?.chat_name || 'AI Screen'
          })
        });
        const body = await parseJson(resp);
        if (!resp.ok) {
          throw new Error(body.message || 'Failed to run screen');
        }

        const screenResultId = body.screen_result_id;
        setActionResults((prev) => ({
          ...prev,
          [message.id]: {
            status: 'success',
            type: 'screen',
            data: body,
            link: screenResultId
              ? `/dashboard/strategies?screen_result=${screenResultId}`
              : '/dashboard'
          }
        }));
        return;
      }

      if (action === 'backtest_configuration') {
        if (backtestConfigIdFromPayload) {
          setActionResults((prev) => ({
            ...prev,
            [message.id]: {
              status: 'success',
              type: 'backtest_config',
              data: { id: backtestConfigIdFromPayload },
              link: '/dashboard/backtest-configs'
            }
          }));
          return;
        }
        if (!backtestConfig) {
          throw new Error('No backtest_configuration returned with the action.');
        }
        const created = await createBacktestConfig({
          name: backtestPayload?.name || 'Chat Backtest Config',
          description: 'Generated via chat',
          config: backtestConfig
        });
        setActionResults((prev) => ({
          ...prev,
          [message.id]: {
            status: 'success',
            type: 'backtest_config',
            data: created,
            link: '/dashboard/backtest-configs'
          }
        }));
        return;
      }

      if (action === 'backtest' || action === 'optimizer') {
        const runType = (isOptimizerAction || optimizerPayload) ? 'optimizer' : 'backtest';
        let backtestConfigId = backtestConfigIdFromPayload;
        if (!backtestConfigId && backtestConfig) {
          const created = await createBacktestConfig({
            name: backtestPayload?.name || 'Chat Backtest Config',
            description: 'Generated via chat',
            config: backtestConfig
          });
          backtestConfigId = created?.id || null;
        } else if (!backtestConfigId) {
          const configs = await fetchBacktestConfigs();
          const defaultCfg = configs.find((cfg) => cfg.is_default);
          backtestConfigId = defaultCfg?.id || configs[0]?.id || null;
        }

        if (!backtestConfigId) {
          throw new Error('No backtest configuration available.');
        }
        const runPayload = {
          name: strategyPayload?.name || 'Chat backtest',
          strategy: strategyConfig,
          backtest_config_id: backtestConfigId
        };
        if (optimizerPayload) {
          runPayload.optimizer = optimizerPayload;
        }
        const resp = await fetch(`${API_BASE}/api/backtests`, {
          method: 'POST',
          headers,
          body: JSON.stringify(runPayload)
        });
        const body = await parseJson(resp);
        if (!resp.ok) {
          const fallbackMessage = runType === 'optimizer' ? 'Failed to run optimizer' : 'Failed to run backtest';
          throw new Error(body.message || fallbackMessage);
        }
        const backtestId = body.backtest_id;
        setActionResults((prev) => ({
          ...prev,
          [message.id]: {
            status: 'success',
            type: runType,
            data: body,
            backtestId,
            link: backtestId ? `/dashboard/strategies?backtest=${backtestId}` : '/dashboard'
          }
        }));
        return;
      }

      if (action === 'chart') {
        const normalizeChartTarget = (value) => {
          const normalized = String(value || '').toLowerCase().trim();
          if (['panel', 'below', 'bottom', 'sub', 'secondary_panel'].includes(normalized)) return 'panel';
          return 'main';
        };
        const normalizeChartAxis = (value) => {
          const normalized = String(value || '').toLowerCase().trim();
          if (['right', 'secondary', 'y2', 'secondary_axis'].includes(normalized)) return 'right';
          return 'left';
        };
        const normalizeChartSeries = (input) => {
          if (!input) return [];
          const items = Array.isArray(input) ? input : [input];
          return items.map((item) => {
            if (typeof item === 'string') {
              const trimmed = item.trim();
              return trimmed ? { formula: trimmed, target: 'main', axis: 'left' } : null;
            }
            if (!item || typeof item !== 'object') return null;
            const formulaText = String(item.formula || item.expression || '').trim();
            if (!formulaText) return null;
            const targetHint = item.target ?? (item.panel ? 'panel' : null) ?? item.location;
            const axisHint = item.axis ?? item.y_axis ?? item.yAxis ?? item.yAxisId;
            return {
              formula: formulaText,
              name: item.name || item.label,
              target: normalizeChartTarget(targetHint),
              axis: normalizeChartAxis(axisHint)
            };
          }).filter(Boolean);
        };
        const symbol = (chartConfig?.symbol || chartConfig?.ticker || '').trim().toUpperCase();
        const rawFormula = (chartConfig?.formula || chartConfig?.expression || '').trim();
        const seriesInput = chartConfig?.series || chartConfig?.formulas || chartConfig?.lines;
        const seriesList = normalizeChartSeries(seriesInput);
        const formula = rawFormula || seriesList[0]?.formula || '';
        const extraSeries = rawFormula ? seriesList : seriesList.slice(1);
        if (!symbol) {
          throw new Error('Chart requires a symbol.');
        }
        if (!formula) {
          throw new Error('Chart requires a formula.');
        }
        const payload = {
          symbol,
          formula
        };
        const assetClass = chartConfig?.asset_class || chartConfig?.assetClass;
        if (assetClass) payload.asset_class = assetClass;
        const startDate = chartConfig?.start_date || chartConfig?.startDate;
        if (startDate) payload.start_date = startDate;
        const endDate = chartConfig?.end_date || chartConfig?.endDate;
        if (endDate) payload.end_date = endDate;
        const maxPoints = chartConfig?.max_points || chartConfig?.maxPoints;
        if (maxPoints !== undefined && maxPoints !== null) {
          payload.max_points = maxPoints;
        }
        const name = chartConfig?.name || chartPayload?.name || message?.chat_name;
        if (name) payload.name = name;

        const resp = await fetch(`${API_BASE}/api/charts`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });
        const body = await parseJson(resp);
        if (!resp.ok) {
          throw new Error(body.message || 'Failed to run chart');
        }
        const resultPayload = body.result || body;
        const chartResultId = resultPayload?.chart_result_id || resultPayload?.id || null;
        let seriesKey = null;
        if (extraSeries.length > 0) {
          seriesKey = `chart-series:${chartResultId || Date.now()}`;
          try {
            localStorage.setItem(seriesKey, JSON.stringify({ series: extraSeries }));
          } catch (err) {
            console.warn('Failed to store chart series config:', err);
            seriesKey = null;
          }
        }
        setActionResults((prev) => ({
          ...prev,
          [message.id]: {
            status: 'success',
            type: 'chart',
            data: body,
            chartResultId,
            link: (() => {
              const baseLink = chartResultId ? `/dashboard/charts?result_id=${chartResultId}` : '/dashboard/charts';
              if (!seriesKey) return baseLink;
              const separator = baseLink.includes('?') ? '&' : '?';
              return `${baseLink}${separator}series_key=${encodeURIComponent(seriesKey)}`;
            })()
          }
        }));
        return;
      }

      if (action === 'research') {
        const resp = await fetch(`${API_BASE}/api/research`, {
          method: 'POST',
          headers,
          body: JSON.stringify(researchConfig)
        });
        const body = await parseJson(resp);
        if (!resp.ok) {
          throw new Error(body.message || 'Failed to run research');
        }
        setActionResults((prev) => ({
          ...prev,
          [message.id]: {
            status: 'success',
            type: 'research',
            data: body
          }
        }));
        return;
      }

      if (action === 'seasonality') {
        const symbol = (seasonalityConfig?.symbol || seasonalityConfig?.ticker || '').trim().toUpperCase();
        if (!symbol) {
          throw new Error('Seasonality requires a symbol.');
        }
        const payload = { symbol };
        const focus = seasonalityConfig?.focus || seasonalityConfig?.period || null;
        if (focus) payload.focus = focus;
        const assetClass = seasonalityConfig?.asset_class || seasonalityConfig?.assetClass;
        if (assetClass) payload.asset_class = assetClass;
        const startDate = seasonalityConfig?.start_date || seasonalityConfig?.startDate;
        if (startDate) payload.start_date = startDate;
        const endDate = seasonalityConfig?.end_date || seasonalityConfig?.endDate;
        if (endDate) payload.end_date = endDate;
        const name = seasonalityConfig?.name || seasonalityPayload?.name || message?.chat_name;
        if (name) payload.name = name;

        const resp = await fetch(`${API_BASE}/api/seasonality`, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });
        const body = await parseJson(resp);
        if (!resp.ok) {
          throw new Error(body.message || 'Failed to run seasonality');
        }
        const resultPayload = body.result || body;
        const seasonalityResultId = resultPayload?.seasonality_result_id || resultPayload?.id || null;
        setActionResults((prev) => ({
          ...prev,
          [message.id]: {
            status: 'success',
            type: 'seasonality',
            data: body,
            seasonalityResultId,
            link: seasonalityResultId
              ? `/dashboard/seasonality?result_id=${seasonalityResultId}`
              : '/dashboard/seasonality'
          }
        }));
      }
    } catch (err) {
      setActionResults((prev) => ({
        ...prev,
        [message.id]: { status: 'error', type: action, error: err.message || 'Action failed' }
      }));
    }
  }, [getSessionHeaders, parseJson]);

  const loadMessages = useCallback(async (chatId) => {
    if (!chatId) {
      setLoadingConversation(false);
      return;
    }
    try {
      setLoadingConversation(true);
      setError('');
      const headers = await getSessionHeaders();
      const res = await fetch(`${API_BASE}/api/chats/${chatId}/messages`, { headers });
      const data = await parseJson(res);
      if (!res.ok) {
        throw new Error(data.message || 'Failed to load conversation');
      }
      setMessages(data.messages || []);
      if (data.chat) {
        setChats((prev) => {
          const filtered = prev.filter((c) => c.id !== data.chat.id);
          return [data.chat, ...filtered];
        });
      }
    } catch (err) {
      setError(err.message || 'Unable to load conversation');
    } finally {
      setLoadingConversation(false);
    }
  }, [getSessionHeaders, parseJson]);

  useEffect(() => {
    loadChats();
    fetchQuota();
  }, [loadChats, fetchQuota]);

  useEffect(() => {
    const stateChatId = location.state?.chatId;
    const queryChatId = new URLSearchParams(location.search).get('chat_id');
    const nextChatId = stateChatId || queryChatId;
    if (nextChatId) {
      setSelectedChatId(nextChatId);
      setMessages([]);
      setLoadingConversation(true);
      setSuggestionsDismissed(true);
      return;
    }
    handleStartNewChat();
  }, [handleStartNewChat, location]);

  useEffect(() => {
    if (selectedChatId) {
      loadMessages(selectedChatId);
    }
  }, [selectedChatId, loadMessages]);

  // Auto-run assistant actions when new assistant messages arrive
  useEffect(() => {
    const pending = messages.find(
      (m) =>
        m.role === 'assistant' &&
        m.action &&
        m.action.toLowerCase() !== 'none' &&
        !processedActionsRef.current.has(m.id)
    );
    if (pending) {
      processedActionsRef.current.add(pending.id);
      executeAction(pending);
    }
  }, [messages, executeAction]);

  const renderActionResult = (message) => {
    const result = actionResults[message.id];
    if (!result) return null;

    if (result.status === 'loading') {
      return (
        <div className="action-result loading">
          <FiLoader className="spin" />
          <span>Running {result.type}...</span>
        </div>
      );
    }

    if (result.status === 'error') {
      return (
        <div className="action-result error">
          <FiAlertTriangle />
          <span>{result.error}</span>
        </div>
      );
    }

    if (result.type === 'strategy') {
      return (
        <div className="action-result success">
          <FiCpu />
          <div>
            <div className="action-result-title">Strategy draft ready</div>
            <div className="action-result-body">Open the builder to edit and run it.</div>
            {result.link && (
              <button
                className="action-button"
                onClick={() => window.open(result.link, '_blank', 'noopener,noreferrer')}
              >
                Open builder
              </button>
            )}
          </div>
        </div>
      );
    }

    if (result.type === 'backtest_config') {
      return (
        <div className="action-result success">
          <FiCpu />
          <div>
            <div className="action-result-title">Backtest configuration saved</div>
            <div className="action-result-body">View it in the Backtest Configurations tab.</div>
            {result.link && (
              <button
                className="action-button"
                onClick={() => window.open(result.link, '_blank', 'noopener,noreferrer')}
              >
                Open configurations
              </button>
            )}
          </div>
        </div>
      );
    }

    if (result.type === 'screen') {
      const tickers = result.data?.selected_tickers ||
        (result.data?.results || [])
          .filter((r) => r.selected)
          .map((r) => r.ticker)
          .filter(Boolean);
      return (
        <div className="action-result success">
          <FiCpu />
          <div>
            <div className="action-result-title">Screen results ready</div>
            {tickers?.length ? (
              <div className="action-result-body">
                Top tickers: {tickers.slice(0, 10).join(', ')}
              </div>
            ) : (
              <div className="action-result-body">Completed without a ticker list.</div>
            )}
            {result.link && (
              <button
                className="action-button"
                onClick={() => window.open(result.link, '_blank', 'noopener,noreferrer')}
              >
                Open results
              </button>
            )}
          </div>
        </div>
      );
    }

    if (result.type === 'backtest') {
      const metrics = result.data?.result?.metrics || result.data?.metrics || {};
      const totalReturn = metrics.total_return ?? metrics.total_return_pct;
      return (
        <div className="action-result success">
          <FiCpu />
          <div>
            <div className="action-result-title">Backtest kicked off</div>
            <div className="action-result-body">
              {Number.isFinite(totalReturn)
                ? `Total return: ${(totalReturn * 100).toFixed(2)}%`
                : (result.data?.message || 'Backtest request accepted.')}
            </div>
            {result.link && (
              <button
                className="action-button"
                onClick={() => window.open(result.link, '_blank', 'noopener,noreferrer')}
              >
                Open results
              </button>
            )}
          </div>
        </div>
      );
    }

    if (result.type === 'optimizer') {
      const optimization = result.data?.result?.optimization || result.data?.optimization || {};
      const bestValueRaw = optimization.best_value ?? optimization.bestValue;
      const bestMetricRaw = optimization.best_metric ?? optimization.bestMetric;
      const bestValue = Number(bestValueRaw);
      const bestMetric = Number(bestMetricRaw);
      const target = optimization.target || null;
      const summaryParts = [];
      if (Number.isFinite(bestValue)) {
        summaryParts.push(`Best x: ${bestValue}`);
      } else if (bestValueRaw !== undefined && bestValueRaw !== null) {
        summaryParts.push(`Best x: ${bestValueRaw}`);
      }
      if (Number.isFinite(bestMetric)) {
        summaryParts.push(`${target || 'Target'}: ${bestMetric}`);
      } else if (bestMetricRaw !== undefined && bestMetricRaw !== null) {
        summaryParts.push(`${target || 'Target'}: ${bestMetricRaw}`);
      } else if (target) {
        summaryParts.push(`Target: ${target}`);
      }
      const summary = summaryParts.length
        ? summaryParts.join(' | ')
        : (result.data?.message || 'Optimizer request accepted.');
      return (
        <div className="action-result success">
          <FiCpu />
          <div>
            <div className="action-result-title">Optimizer kicked off</div>
            <div className="action-result-body">{summary}</div>
            {result.link && (
              <button
                className="action-button"
                onClick={() => window.open(result.link, '_blank', 'noopener,noreferrer')}
              >
                Open results
              </button>
            )}
          </div>
        </div>
      );
    }

    if (result.type === 'chart') {
      const payload = result.data?.result || result.data || {};
      const summary = [payload.symbol || payload.ticker, payload.formula].filter(Boolean).join(' · ');
      const dateRange = payload.start_date && payload.end_date
        ? `${payload.start_date} to ${payload.end_date}`
        : null;
      return (
        <div className="action-result success">
          <FiCpu />
          <div>
            <div className="action-result-title">Chart ready</div>
            <div className="action-result-body">
              {summary || 'Chart completed.'}
              {dateRange ? ` (${dateRange})` : ''}
            </div>
            {result.link && (
              <button
                className="action-button"
                onClick={() => window.open(result.link, '_blank', 'noopener,noreferrer')}
              >
                Open chart
              </button>
            )}
          </div>
        </div>
      );
    }

    if (result.type === 'seasonality') {
      const payload = result.data?.result || result.data || {};
      const summary = [payload.symbol || payload.ticker, payload.focus].filter(Boolean).join(' · ');
      const dateRange = payload.start_date && payload.end_date
        ? `${payload.start_date} to ${payload.end_date}`
        : null;
      return (
        <div className="action-result success">
          <FiCpu />
          <div>
            <div className="action-result-title">Seasonality ready</div>
            <div className="action-result-body">
              {summary || 'Seasonality completed.'}
              {dateRange ? ` (${dateRange})` : ''}
            </div>
            {result.link && (
              <button
                className="action-button"
                onClick={() => window.open(result.link, '_blank', 'noopener,noreferrer')}
              >
                Open seasonality
              </button>
            )}
          </div>
        </div>
      );
    }

    if (result.type === 'research') {
      const payload = result.data?.result || result.data || {};
      const output = payload.result ?? payload.output ?? payload.value ?? payload;
      const logs = payload.logs || [];
      const outputText = typeof output === 'string'
        ? output
        : JSON.stringify(output, null, 2);
      return (
        <div className="action-result success">
          <FiCpu />
          <div>
            <div className="action-result-title">Research complete</div>
            <div className="action-result-body">
              <pre className="action-result-code">{outputText}</pre>
              {logs.length > 0 && (
                <pre className="action-result-code">{logs.join('\n')}</pre>
              )}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderMessage = (message) => {
    const isUser = message.role === 'user';
    const actionName = (message.action || '').toLowerCase();
    const hasAction = actionName && actionName !== 'none';
    const friendlyAction = actionName ? actionName.replace(/_/g, ' ') : message.action;
    const actionButtonLabel = actionName === 'backtest'
      ? 'Run backtest'
      : actionName === 'optimizer'
        ? 'Run optimizer'
      : actionName === 'strategy_configuration'
        ? 'Open builder'
        : actionName === 'backtest_configuration'
          ? 'Save backtest config'
          : actionName === 'chart'
            ? 'Run chart'
          : actionName === 'seasonality'
            ? 'Run seasonality'
            : actionName === 'research'
              ? 'Run research'
              : 'Run screen';
    return (
      <div key={message.id} className={`chat-message ${isUser ? 'user' : 'assistant'}`}>
        <div className="chat-message-header">
          <span className="role">{isUser ? 'You' : 'FactorBench'}</span>
          {message.created_at && <span className="timestamp">{message.created_at}</span>}
        </div>
        <div className="chat-bubble">
          <div className="chat-text">
            {message.content}
          </div>
          {!isUser && hasAction && (
            <div className="action-chip">
              Action: {friendlyAction}
            </div>
          )}
          {!isUser && hasAction && (
            <button
              className="action-button"
              onClick={() => executeAction(message)}
              disabled={actionResults[message.id]?.status === 'loading'}
            >
              <FiPlay size={16} />
              <span>{actionButtonLabel}</span>
            </button>
          )}
          {!isUser && renderActionResult(message)}
        </div>
      </div>
    );
  };

  const aiUsed = aiQuota?.used ?? 0;
  const aiLimit = aiQuota?.limit ?? null;
  const aiResets = aiQuota?.resets_at;
  const aiProgress = aiLimit ? Math.min(100, Math.round((aiUsed / aiLimit) * 100)) : 0;
  const showSuggestions = !suggestionsDismissed && messages.length === 0 && !loadingConversation;
  const isChatActive = loadingConversation || messages.length > 0;

  if (loading) {
    return (
      <div className="chat-page">
        <div className="chat-layout">
          <div className="chat-main">
            <div className="chat-empty">Loading chats...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-page">
      {!isPremium && (
        <div className="chat-upgrade-block">
          <PremiumSignupBanner isPremium={isPremium} />
        </div>
      )}
      <div className="chat-layout">
        <div className={`chat-main ${isChatActive ? 'chat-main-active' : 'chat-main-empty'}`}>
          <div className="chat-main-header">
            <div className="chat-header-left">
              <div className="chat-name">{activeChat?.title || 'Chat'}</div>
              <div className="chat-subtitle">
                {currentUser?.email ? `Signed in as ${currentUser.email}` : 'Ask about strategies, watchlists, backtests, charts, or seasonality.'}
              </div>
            </div>
            <div className="chat-header-right">
              <button className="ghost-button" onClick={handleStartNewChat}>
                <FiPlus />
                <span>New</span>
              </button>
              {aiLimit && (
                <div className="ai-quota-chip">
                  <div className="ai-quota-text">
                    <strong>AI credits:</strong> {aiUsed} / {aiLimit}
                  </div>
                  {aiResets && <div className="ai-quota-sub">Resets {aiResets}</div>}
                  <div className="ai-quota-bar">
                    <div className="ai-quota-bar-fill" style={{ width: `${aiProgress}%` }} />
                  </div>
                </div>
              )}
            </div>
          </div>
          {(truncatedNotice || !aiLimit) && (
            <div className="chat-main-meta">
              {truncatedNotice && (
                <div className="truncate-notice">
                  <FiAlertTriangle />
                  <span>Message trimmed to {MAX_MESSAGE_CHARS} characters.</span>
                </div>
              )}
              {!aiLimit && (
                <div className="chat-subtitle small-note">
                  AI credits are consumed per message; usage is tracked per month.
                </div>
              )}
            </div>
          )}

          <div className={`chat-messages ${isChatActive ? 'chat-messages-active' : ''} ${showSuggestions ? 'chat-messages-suggestions' : ''}`}>
            {loadingConversation && messages.length === 0 ? (
              <div className="chat-empty">Loading conversation...</div>
            ) : showSuggestions ? (
              <div className="chat-suggestions">
                <div className="chat-suggestions-header">
                  <div>
                    <div className="chat-suggestions-title">Chat suggestions</div>
                    <div className="chat-suggestions-subtitle">
                      Pick a prompt or ask your own below to get started.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="icon-button chat-suggestions-dismiss"
                    onClick={() => setSuggestionsDismissed(true)}
                    aria-label="Hide chat suggestions"
                  >
                    <FiX />
                  </button>
                </div>
                <div className="chat-example-list">
                  {EXAMPLE_PROMPTS.map((example) => {
                    const Icon = example.icon;
                    return (
                      <button
                        key={example.id}
                        type="button"
                        className="chat-bubble chat-example-bubble"
                        onClick={() => handleSend(null, example.text)}
                        disabled={sending}
                      >
                        {Icon && (
                          <span className="chat-example-icon" aria-hidden="true">
                            <Icon />
                          </span>
                        )}
                        <span className="chat-example-text">{example.text}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="chat-empty">
                No messages yet. Ask FactorBench to build a strategy, run a chart, or check seasonality.
              </div>
            ) : (
              messages.map(renderMessage)
            )}
          </div>

          <form className="chat-input-bar" onSubmit={handleSend}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Send a message..."
              maxLength={MAX_MESSAGE_CHARS}
              rows={3}
              disabled={sending}
            />
            <div className="input-actions">
              {error && <div className="error-text">{error}</div>}
              <button type="submit" className="primary-button send-button" disabled={sending || !input.trim()}>
                {sending ? <FiLoader className="spin" /> : <FiSend />}
                <span>{sending ? 'Sending' : 'Send'}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="home-history-layout">
        <HistorySection
          onChatDeleted={handleHistoryChatDeleted}
          onChatSelected={handleHistoryChatSelected}
        />
      </div>
    </div>
  );
};

export default HomePage;
